/**
 * AtomicAssets NFT helpers — account inventory and media detection.
 */

import { AtomicAssetsAPIClient } from '@wharfkit/atomicassets'
import { APIClient, FetchProvider, Name, UInt64 } from '@wharfkit/antelope'
import type { Session } from './session.js'
import { IPFS_GATEWAYS } from './ipfs-gateways.js'

export const ATOMICASSETS_API = 'https://eos.api.atomicassets.io'
export const ATOMICASSETS_CONTRACT = 'atomicassets'

export type NftAsset = {
  assetId: string
  /** Current owner on chain — empty when the API did not report one. */
  owner: string
  name: string
  collection: string
  schema: string
  mediaUrl: string | null
  mediaType: 'image' | 'video' | 'unknown'
  templateId: string | null
  data: Record<string, unknown>
}

function toPlainRecord(obj: unknown): Record<string, unknown> {
  if (!obj || typeof obj !== 'object') return {}
  const o = obj as { toJSON?: () => unknown }
  if (typeof o.toJSON === 'function') {
    const j = o.toJSON()
    if (j && typeof j === 'object' && !Array.isArray(j)) return j as Record<string, unknown>
  }
  return { ...(obj as Record<string, unknown>) }
}

function normalizeMediaValue(raw: unknown): string | null {
  if (raw == null) return null
  const s = typeof raw === 'string' ? raw.trim() : String(raw).trim()
  if (!s) return null
  if (s.startsWith('http://') || s.startsWith('https://')) return s
  if (s.startsWith('ipfs://')) {
    const path = s.slice(7).replace(/^ipfs\//, '')
    return `${IPFS_GATEWAYS[0]}${path}`
  }
  if (s.startsWith('Qm') || s.startsWith('bafy')) {
    return `${IPFS_GATEWAYS[0]}${s}`
  }
  return null
}

function mediaTypeFromUrl(url: string): NftAsset['mediaType'] {
  const path = url.split('?')[0].toLowerCase()
  if (/\.(mp4|webm)$/i.test(path)) return 'video'
  if (/\.(png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i.test(path)) return 'image'
  if (/\./.test(path.split('/').pop() ?? '')) return 'image'
  return 'unknown'
}

export function detectMediaUrl(data: Record<string, unknown>): {
  url: string | null
  type: NftAsset['mediaType']
} {
  const keys = ['img', 'image', 'video', 'media', 'picture', 'artwork', 'icon'] as const
  for (const k of keys) {
    if (k in data && data[k] != null) {
      const url = normalizeMediaValue(data[k])
      if (url) {
        return { url, type: mediaTypeFromUrl(url) }
      }
    }
  }
  return { url: null, type: 'unknown' }
}

let cachedClient: AtomicAssetsAPIClient | null = null

function getAtomicClient(): AtomicAssetsAPIClient {
  if (!cachedClient) {
    cachedClient = new AtomicAssetsAPIClient(
      new APIClient({ provider: new FetchProvider(ATOMICASSETS_API) }),
    )
  }
  return cachedClient
}

function readAssetId(raw: unknown): string {
  if (raw == null) return ''
  if (typeof raw === 'object' && raw !== null && 'toString' in raw) {
    return String((raw as { toString: () => string }).toString())
  }
  return String(raw)
}

function mapApiAssetToNft(asset: unknown): NftAsset | null {
  if (!asset || typeof asset !== 'object') return null
  const a = asset as Record<string, unknown>

  const immutable = toPlainRecord(a.immutable_data)
  const mutable = toPlainRecord(a.mutable_data)
  const dataField = toPlainRecord(a.data)
  const merged: Record<string, unknown> = { ...immutable, ...mutable, ...dataField }

  const { url: mediaUrl, type: mediaType } = detectMediaUrl(merged)

  const collectionObj = a.collection as Record<string, unknown> | undefined
  const schemaObj = a.schema as Record<string, unknown> | undefined
  const templateObj = a.template as Record<string, unknown> | undefined

  const collectionName = collectionObj?.collection_name
  const schemaName = schemaObj?.schema_name
  const templateId = templateObj?.template_id

  const name =
    (typeof a.name === 'string' && a.name) ||
    (typeof merged.name === 'string' && merged.name) ||
    (typeof merged.title === 'string' && merged.title) ||
    'NFT'

  return {
    assetId: readAssetId(a.asset_id),
    owner: typeof a.owner === 'string' ? a.owner : '',
    name: String(name),
    collection: collectionName != null ? String(collectionName) : '',
    schema: schemaName != null ? String(schemaName) : '',
    mediaUrl,
    mediaType,
    templateId: templateId != null ? String(templateId) : null,
    data: merged,
  }
}

export async function loadAccountNfts(accountName: string): Promise<NftAsset[]> {
  const trimmed = accountName?.trim()
  if (!trimmed) return []
  try {
    const client = getAtomicClient()
    const res = await client.atomicassets.v1.get_assets({
      owner: [trimmed],
      limit: 100,
      sort: 'transferred',
      order: 'desc',
    })
    const rows = (res as { data?: unknown[] }).data
    if (!Array.isArray(rows)) return []
    return rows.map(mapApiAssetToNft).filter((x): x is NftAsset => x !== null && x.assetId.length > 0)
  } catch (e) {
    console.error('[chain-eos] loadAccountNfts failed:', e)
    return []
  }
}

export async function loadNftsByCollection(
  accountName: string,
  collection: string,
): Promise<NftAsset[]> {
  const t = accountName?.trim()
  const c = collection?.trim()
  if (!t || !c) return []
  try {
    const client = getAtomicClient()
    const res = await client.atomicassets.v1.get_assets({
      owner: [t],
      collection_name: [c],
      limit: 100,
      sort: 'transferred',
      order: 'desc',
    })
    const rows = (res as { data?: unknown[] }).data
    if (!Array.isArray(rows)) return []
    return rows.map(mapApiAssetToNft).filter((x): x is NftAsset => x !== null && x.assetId.length > 0)
  } catch (e) {
    console.error('[chain-eos] loadNftsByCollection failed:', e)
    return []
  }
}

/**
 * Look up specific assets by id — used to render NFTs attached to chat messages.
 *
 * Goes through the REST endpoint rather than the typed client: the `ids` filter is
 * a plain query parameter and the typed wrapper does not expose it consistently.
 */
export async function loadNftsByIds(assetIds: string[]): Promise<NftAsset[]> {
  const ids = [...new Set(assetIds.map(id => String(id).trim()).filter(id => /^[0-9]{6,20}$/.test(id)))]
  if (ids.length === 0) return []
  try {
    const url = `${ATOMICASSETS_API}/atomicassets/v1/assets?ids=${ids.join(",")}&limit=${Math.min(ids.length, 100)}`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const body = (await res.json()) as { data?: unknown[] }
    if (!Array.isArray(body.data)) return []
    return body.data.map(mapApiAssetToNft).filter((x): x is NftAsset => x !== null && x.assetId.length > 0)
  } catch (e) {
    console.warn("[chain-eos] loadNftsByIds failed:", e)
    return []
  }
}

// ─── Transfer ─────────────────────────────────────────────────────────────────
// Sending an NFT is a plain AtomicAssets `transfer` action, authorized only by
// the sender (`from`) — the recipient does not co-sign. AtomicAssets pays the
// RAM for the row landing in the recipient's scope out of its own reserve
// (funded by minting fees), so the sender is never on the hook for the
// receiving side's storage. What the sender DOES pay for is their own CPU/NET
// to push the transaction — same as any other signed action in this wallet,
// covered automatically by the Fuel transact plugin (see session.ts) if the
// account is short.

export interface NftTransferParams {
  from: string
  to: string
  assetId: string
  memo?: string
}

export interface NftTransferResult {
  txid: string
  blockNum?: number
}

export async function transferNft(
  session: Session,
  params: NftTransferParams,
): Promise<NftTransferResult> {
  const { from, to, assetId, memo = '' } = params

  const result = await session.transact({
    action: {
      account: ATOMICASSETS_CONTRACT,
      name: 'transfer',
      authorization: [{ actor: from, permission: 'active' }],
      data: {
        from: Name.from(from),
        to: Name.from(to),
        asset_ids: [UInt64.from(assetId)],
        memo,
      },
    },
  })

  const txid = (result.response as { transaction_id?: string })?.transaction_id ?? ''
  const blockNum = (result.response as { processed?: { block_num?: number } })?.processed?.block_num
  return { txid, blockNum }
}

/** Returns null if valid, or a key string for an error message if not. */
export function validateNftTransfer(params: NftTransferParams): string | null {
  if (!params.assetId) return 'noAsset'
  if (!params.to || params.to.length < 1) return 'noRecipient'
  if (params.to.length > 13) return 'invalidAccount'
  if (!/^[a-z1-5.]+$/.test(params.to)) return 'invalidAccount'
  if (params.from === params.to) return 'selfTransfer'
  return null
}
